/** Optional practical fields for a single itinerary day. Every key is omitted
 *  unless a real value exists — never invent driving times, hotels, elevation,
 *  activities, or landmarks to fill the shape. */
export type TourItineraryDayDetails = {
  /** Longer “Today’s journey” copy. Distinct from the short `body` summary. */
  details?: string;
  /**
   * This day's own photograph.
   *
   * Optional, and unset on every one of the 149 existing days: until it
   * existed the loader handed each day the tour's hero image, so a seven-day
   * itinerary showed the same picture seven times. Where it is still unset the
   * hero remains the fallback — a repeated photo is honest, a wrong one is
   * not.
   */
  image?: string;
  startLocation?: string;
  endLocation?: string;
  /** Preformatted route such as "Islamabad → Taxila → Naran". */
  route?: string;
  driving?: string;
  activity?: string;
  elevation?: string;
  includedActivities?: string[];
  optionalActivities?: string[];
  highlights?: string[];
  notes?: string;
};

export const ITINERARY_ACTIVITY_LEVELS = ["Easy", "Moderate", "Active", "Challenging"] as const;
export type ItineraryActivityLevel = (typeof ITINERARY_ACTIVITY_LEVELS)[number];

function optionalTrimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function optionalStringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return items.length > 0 ? items : undefined;
}

/** Pulls optional day-detail fields from stored JSON. Empty strings and empty
 *  arrays become `undefined` so renderers can skip the label entirely. */
export function readOptionalItineraryFields(row: Record<string, unknown>): TourItineraryDayDetails {
  const details: TourItineraryDayDetails = {
    details: optionalTrimmedString(row.details) ?? optionalTrimmedString(row.description),
    image: optionalTrimmedString(row.image),
    startLocation: optionalTrimmedString(row.startLocation),
    endLocation: optionalTrimmedString(row.endLocation),
    route: optionalTrimmedString(row.route),
    driving: optionalTrimmedString(row.driving) ?? optionalTrimmedString(row.drivingTime),
    activity: optionalTrimmedString(row.activity) ?? optionalTrimmedString(row.activityLevel),
    elevation: optionalTrimmedString(row.elevation),
    includedActivities: optionalStringList(row.includedActivities),
    optionalActivities: optionalStringList(row.optionalActivities),
    highlights: optionalStringList(row.highlights),
    notes: optionalTrimmedString(row.notes),
  };
  return Object.fromEntries(
    Object.entries(details).filter(([, value]) => value !== undefined),
  ) as TourItineraryDayDetails;
}

export function itineraryRouteLabel(
  day: Pick<TourItineraryDayDetails, "route" | "startLocation" | "endLocation">,
): string | undefined {
  if (day.route) return day.route;
  if (day.startLocation && day.endLocation) return `${day.startLocation} → ${day.endLocation}`;
  if (day.startLocation) return `Start: ${day.startLocation}`;
  if (day.endLocation) return `End: ${day.endLocation}`;
  return undefined;
}

export function itineraryCompactMeta(
  day: Pick<TourItineraryDayDetails, "driving" | "activity" | "elevation">,
): string | undefined {
  const bits = [day.driving, day.activity, day.elevation].filter(
    (value): value is string => Boolean(value),
  );
  return bits.length > 0 ? bits.join(" · ") : undefined;
}

/** True when a day has extra fields beyond title, body, stay, and meals.
 *  Stay and meals stay in the collapsed summary and must not count here. */
export function itineraryHasExpandedDetails(
  day: TourItineraryDayDetails & { stay?: string; meals?: string },
): boolean {
  return Boolean(
    day.details ||
      itineraryRouteLabel(day) ||
      day.driving ||
      day.activity ||
      day.elevation ||
      day.includedActivities?.length ||
      day.optionalActivities?.length ||
      day.highlights?.length ||
      day.notes,
  );
}
